import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Shared";
import {
  SquaresFour, Package, WarningOctagon, ClockCounterClockwise, SignOut, UsersThree, HandArrowUp,
  FirstAidKit, CalendarCheck, ChartBar, Heartbeat, GlobeHemisphereWest, ArrowsLeftRight,
} from "@phosphor-icons/react";

const GROUPS = {
  clinic: [
    {
      section: "Clinic",
      links: [
        { to: "/clinic/portal", label: "My Portal", icon: Heartbeat, id: "portal", perm: "portal:read" },
        { to: "/clinic/patients", label: "Patients", icon: FirstAidKit, id: "patients", perm: "patient:read" },
        { to: "/clinic/appointments", label: "Appointments", icon: CalendarCheck, id: "appointments", perm: "appointment:read" },
        { to: "/clinic/reports", label: "Clinic reports", icon: ChartBar, id: "clinic-reports", perm: "report:read" },
      ],
    },
  ],
  inventory: [
    {
      section: "Inventory",
      links: [
        { to: "/inventory", label: "Dashboard", icon: SquaresFour, id: "dashboard", perm: "transaction:create" },
        { to: "/inventory/items", label: "Stock Items", icon: Package, id: "inventory", perm: "transaction:create" },
        { to: "/inventory/take-out", label: "Take from store", icon: HandArrowUp, id: "take-out", perm: "transaction:create" },
        { to: "/inventory/alerts", label: "Alerts", icon: WarningOctagon, id: "alerts", perm: "transaction:create" },
        { to: "/inventory/history", label: "History", icon: ClockCounterClockwise, id: "history", perm: "transaction:create" },
      ],
    },
    {
      section: "Admin",
      links: [
        { to: "/inventory/website", label: "Website", icon: GlobeHemisphereWest, id: "website", perm: "settings:manage" },
        { to: "/inventory/users", label: "Users & roles", icon: UsersThree, id: "users", perm: "user:manage" },
      ],
    },
  ],
};
const SUBTITLE = { clinic: "P&O Clinic System", inventory: "P&O Inventory System" };
const ROLE_LABEL = { admin: "Admin", doctor: "Doctor", subadmin: "Doctor", staff: "Staff", patient: "Patient" };

export default function Sidebar({ variant = "inventory" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const has = (perm) => !perm || user?.permissions?.includes(perm);
  const groups = GROUPS[variant] || GROUPS.inventory;

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <aside className="w-64 shrink-0 bg-card border-r border-border flex flex-col h-screen sticky top-0" data-testid="app-sidebar">
      <div className="px-5 py-5 border-b border-border">
        <img src="/raphadon-logo-trim.png" alt="Raphadon Global Healthcare" className="w-full h-auto object-contain" />
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">{SUBTITLE[variant]}</p>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
        {groups.map(({ section, links }) => {
          const visible = links.filter((l) => has(l.perm));
          if (visible.length === 0) return null;
          return (
            <div key={section}>
              <p className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-semibold">{section}</p>
              <div className="space-y-1">
                {visible.map(({ to, label, icon: Icon, id }) => (
                  <NavLink key={to} to={to} data-testid={`nav-${id}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                        isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      }`}>
                    <Icon size={19} weight="regular" />
                    {label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-border">
        {user?.role === "admin" && (
          <Link to={variant === "clinic" ? "/inventory" : "/clinic"} data-testid="workspace-switch"
            className="w-full flex items-center gap-3 px-3 py-2.5 mb-1 rounded-md text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 transition-colors duration-200">
            <ArrowsLeftRight size={19} />
            {variant === "clinic" ? "Switch to Inventory" : "Switch to Clinic"}
          </Link>
        )}
        <div className="flex items-center gap-3 px-3 py-2 mb-1">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium truncate text-foreground" data-testid="sidebar-username">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate" data-testid="sidebar-role">{ROLE_LABEL[user?.role] || user?.role}</p>
          </div>
        </div>
        <button onClick={handleLogout} data-testid="logout-button"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors duration-200">
          <SignOut size={19} /> Sign out
        </button>
      </div>
    </aside>
  );
}
